/**
 * Date utility functions for formatting and relative time calculations.
 * All date logic uses user's local timezone (browser/device).
 * Store UTC where needed; display in local.
 */

/**
 * Get today's date in user's local timezone as YYYY-MM-DD.
 * Use for claim date, misi harian, streaks — NOT toISOString() which is UTC.
 * @returns {string} YYYY-MM-DD
 */
export function getLocalDateString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Get today's date in DD-MM-YYYY for AlAdhan API.
 * @returns {string} DD-MM-YYYY
 */
export function getLocalDateDDMMYYYY() {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, '0');
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const y = d.getFullYear();
  return `${day}-${m}-${y}`;
}

/**
 * Get user's timezone (e.g. "Asia/Jakarta").
 * @returns {string}
 */
export function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Format date to Indonesian locale
 * @param {string|Date} timestamp - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDate = (timestamp) => {
    if (!timestamp) return 'Tanggal tidak diketahui';
    try {
        return new Date(timestamp).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (e) {
        return 'Tanggal tidak valid';
    }
};

/**
 * Format date with time to Indonesian locale
 * @param {string|Date} timestamp - Date string or Date object
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Tanggal tidak diketahui';
    try {
        return new Date(timestamp).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return 'Tanggal tidak valid';
    }
};

/**
 * Get relative time string (e.g., "2 hari lalu", "3 jam lalu")
 * @param {string|Date} timestamp - Date string or Date object
 * @returns {string} Relative time string in Indonesian
 */
export const getRelativeTime = (timestamp) => {
    if (!timestamp) return 'Tanggal tidak diketahui';
    
    try {
        const now = new Date();
        const date = new Date(timestamp);
        const diffInMs = now - date;
        const diffInSeconds = Math.floor(diffInMs / 1000);
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);
        const diffInWeeks = Math.floor(diffInDays / 7);
        const diffInMonths = Math.floor(diffInDays / 30);
        const diffInYears = Math.floor(diffInDays / 365);

        if (diffInSeconds < 60) {
            return 'Baru saja';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes} ${diffInMinutes === 1 ? 'menit' : 'menit'} lalu`;
        } else if (diffInHours < 24) {
            return `${diffInHours} ${diffInHours === 1 ? 'jam' : 'jam'} lalu`;
        } else if (diffInDays < 7) {
            return `${diffInDays} ${diffInDays === 1 ? 'hari' : 'hari'} lalu`;
        } else if (diffInWeeks < 4) {
            return `${diffInWeeks} ${diffInWeeks === 1 ? 'minggu' : 'minggu'} lalu`;
        } else if (diffInMonths < 12) {
            return `${diffInMonths} ${diffInMonths === 1 ? 'bulan' : 'bulan'} lalu`;
        } else if (diffInYears < 2) {
            return `${diffInYears} ${diffInYears === 1 ? 'tahun' : 'tahun'} lalu`;
        } else {
            // For very old dates, return formatted date
            return formatDate(timestamp);
        }
    } catch (e) {
        return formatDate(timestamp);
    }
};

/**
 * Estimate reading time based on content length
 * @param {string} content - HTML or plain text content
 * @param {number} wordsPerMinute - Average reading speed (default: 200 words/min for Indonesian)
 * @returns {string} Estimated reading time in Indonesian
 */
export const getReadingTime = (content, wordsPerMinute = 200) => {
    if (!content) return '';
    
    try {
        // Remove HTML tags and get plain text
        const plainText = content.replace(/<[^>]*>/g, '');
        // Count words (split by whitespace and filter empty strings)
        const wordCount = plainText.trim().split(/\s+/).filter(word => word.length > 0).length;
        
        if (wordCount === 0) return '';
        
        const minutes = Math.ceil(wordCount / wordsPerMinute);
        
        if (minutes === 1) {
            return '1 menit baca';
        } else if (minutes < 60) {
            return `${minutes} menit baca`;
        } else {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            if (remainingMinutes === 0) {
                return `${hours} ${hours === 1 ? 'jam' : 'jam'} baca`;
            }
            return `${hours} ${hours === 1 ? 'jam' : 'jam'} ${remainingMinutes} menit baca`;
        }
    } catch (e) {
        return '';
    }
};
